import { ReactNode, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Activity } from 'lucide-react';
import MLBPlayerCardModal from '@/components/mlb-beta/MLBPlayerCardModal';
import MLBPlayerAvatar, { positionColors } from '@/components/mlb-beta/MLBPlayerAvatar';
import { formatPositionDisplay } from '@/lib/positionEligibility';
import ProbablePitcherBadge from '@/components/mlb-beta/ProbablePitcherBadge';
import type { PitcherStartInfo } from '@/hooks/useProbablePitchers';

export interface MLBPlayerRowProps {
  /** Player DB id for the card modal */
  playerId?: string;
  /** Player name */
  name: string;
  /** Position abbreviation (SP, OF, SS, etc.) */
  position: string;
  /** MLB team abbreviation (NYY, LAD, etc.) */
  team?: string | null;
  /** Headshot URL */
  headshotUrl?: string | null;

  /** Slot label shown before the avatar (e.g. SP1, BN, IL) */
  slot?: string;
  /** Status label (e.g. "10-Day IL") — shown as a red badge */
  statusLabel?: string | null;
  /** Position eligibility array, e.g. ["1B", "OF"] */
  positionEligibility?: string[];
  /** Extra info line (e.g. "• 32 GP") */
  extraInfo?: string;

  /** Right-side stat display */
  statValue?: number | null;
  statLabel?: string;
  /** Point delta for live scoring */
  statDelta?: number;

  /** Whether this is a live game with activity indicator */
  isLive?: boolean;
  /** Whether this row is a starter (non-starter rows are dimmed) */
  isStarter?: boolean;
  /** Whether to dim the row (for bench in matchup view) */
  dimmed?: boolean;

  /** Highlight states */
  highlighted?: boolean;
  highlightColor?: 'primary' | 'destructive';

  /** Avatar size */
  avatarSize?: 'sm' | 'md' | 'lg';

  /** Actions rendered on the right side */
  actions?: ReactNode;
  /** Probable pitcher / two-start info */
  startInfo?: PitcherStartInfo | null;
  /** Whether this player's game is postponed */
  gamePostponed?: boolean;

  /** Click handler */
  onClick?: () => void;
  /** Additional className */
  className?: string;
}

const MLBPlayerRow = ({
  playerId,
  name,
  position,
  team,
  headshotUrl,
  positionEligibility,
  slot,
  statusLabel,
  extraInfo,
  statValue,
  statLabel = 'TP',
  statDelta,
  isLive,
  isStarter = true,
  dimmed,
  highlighted,
  highlightColor = 'primary',
  avatarSize = 'md',
  actions,
  startInfo,
  gamePostponed,
  onClick,
  className,
}: MLBPlayerRowProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const hasDelta = statDelta !== undefined && statDelta !== 0;

  return (
    <div
      className={cn(
        "flex items-center gap-3 px-4 py-2.5 border-t border-border/30 transition-colors",
        onClick && "cursor-pointer hover:bg-muted/30",
        !isStarter && "opacity-60",
        dimmed && "opacity-50",
        highlighted && highlightColor === 'primary' && "bg-primary/10",
        highlighted && highlightColor === 'destructive' && "bg-destructive/5",
        hasDelta && statDelta! > 0 && "bg-primary/10",
        hasDelta && statDelta! < 0 && "bg-destructive/10",
        className,
      )}
      onClick={onClick}
    >
      {/* Slot Badge */}
      {slot && (
        <Badge variant="outline" className={cn(
          "text-[10px] px-1.5 py-0 h-5 w-10 justify-center font-mono shrink-0",
          slot === 'IL' && "border-destructive/50 text-destructive"
        )}>
          {slot}
        </Badge>
      )}

      {/* Player Avatar */}
      <MLBPlayerAvatar
        name={name}
        headshotUrl={headshotUrl}
        position={position}
        size={avatarSize}
      />

      {/* Player Info */}
      <div className="flex-1 min-w-0" style={{ minWidth: '7rem' }}>
        <div className="flex items-center gap-1.5">
          <span
            className={cn("text-sm font-medium text-foreground truncate", playerId && "cursor-pointer hover:underline hover:text-primary transition-colors")}
            onClick={(e) => { if (playerId) { e.stopPropagation(); setModalOpen(true); } }}
          >{name}</span>
          {isLive && isStarter && statValue !== undefined && statValue > 0 && (
            <Activity className="w-2.5 h-2.5 text-primary animate-pulse shrink-0" />
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className={cn("text-[9px] px-1 py-0 h-4", positionColors[position])}>
            {formatPositionDisplay(position, positionEligibility)}
          </Badge>
          <span className="text-[10px] text-muted-foreground">{team || 'FA'}</span>
          {!isStarter && slot !== 'IL' && (
            <span className="text-[9px] text-muted-foreground">(BN)</span>
          )}
          {statusLabel && (
            <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 border-destructive/40 text-destructive bg-destructive/10">
              {statusLabel}
            </Badge>
          )}
          {gamePostponed && (
            <Badge variant="outline" className="text-[8px] px-1 py-0 h-3.5 border-yellow-500/60 text-yellow-500 bg-yellow-500/10 font-semibold shrink-0">PPD</Badge>
          )}
          {extraInfo && (
            <span className="text-[10px] text-muted-foreground">{extraInfo}</span>
          )}
        </div>
        {startInfo && <ProbablePitcherBadge startInfo={startInfo} compact />}
      </div>

      {/* Stat Display */}
      {statValue !== undefined && statValue !== null && (
        <div
          className={cn("text-right shrink-0", playerId && "cursor-pointer hover:opacity-70 transition-opacity")}
          onClick={(e) => { if (playerId) { e.stopPropagation(); setModalOpen(true); } }}
          title={playerId ? "View scoring breakdown" : undefined}
        >
          <div className="flex items-center gap-1.5 justify-end">
            {hasDelta && (
              <span className={cn(
                "text-[9px] font-semibold tabular-nums animate-bounce",
                statDelta! > 0 ? "text-primary" : "text-destructive"
              )}>
                {statDelta! > 0 ? '+' : ''}{statDelta!.toFixed(1)}
              </span>
            )}
            <span className={cn(
              "text-sm font-bold tabular-nums",
              statValue > 0 ? "text-primary" : statValue < 0 ? "text-destructive" : "text-muted-foreground"
            )}>
              {statValue.toFixed(1)}
            </span>
          </div>
          {statLabel && (
            <span className="text-[10px] text-muted-foreground block">{statLabel}</span>
          )}
        </div>
      )}

      {/* Actions */}
      {actions && (
        <div className="flex items-center gap-1 shrink-0">
          {actions}
        </div>
      )}

      {/* Player Card Modal */}
      {playerId && (
        <MLBPlayerCardModal
          playerId={playerId}
          open={modalOpen}
          onOpenChange={setModalOpen}
        />
      )}
    </div>
  );
};

export default MLBPlayerRow;
export { positionColors };
